import { type FC, type ReactNode } from "react"
import styled from "styled-components"

import { type RecentEvent } from "@forento/core/models/dashboard"
import { exhaustiveGuard } from "@forento/core/utilities/switch"

import Button from "~/components/Button"
import { toRelativeDateString } from "~/utilities/date"
import routes from "~/utilities/routes"

const Event: FC<{ event: RecentEvent }> = ({ event }) => (
	<Container>
		<Text>{getEventText(event)}</Text>
		<Date>{toRelativeDateString(event.date, "narrow")}</Date>
	</Container>
)

function getEventText(event: RecentEvent): ReactNode {
	if (event.type === "community-post") {
		return (
			<>
				<Link onClick={routes.community.profile(event.profile.id)}>{event.profile.name}</Link> created a{" "}
				<Strong>community post</Strong>
			</>
		)
	}

	const userLink = <Link onClick={routes.user.detail(event.user.id)}>{event.user.name}</Link>
	switch (event.type) {
		case "user":
			return <>{userLink} signed up</>
		case "order":
			return (
				<>
					{userLink} purchased <Strong>{event.productName}</Strong>
				</>
			)
		case "subscription":
			return (
				<>
					{userLink} subscribed to <Strong>{event.productName}</Strong>
				</>
			)
		case "certificate":
		case "community-profile":
			return (
				<>
					{userLink} created a <Strong>community profile</Strong>
				</>
			)
		default:
			exhaustiveGuard(event)
	}
}

const Container = styled.div`
	padding: 16px 18px;
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 16px;

	&:not(:last-child) {
		padding-bottom: 15px;
		border-bottom: 1px solid #efefef;
	}

	@media (max-width: 640px) {
		flex-direction: column;
		gap: 3px;
		padding: 14px 16px;

		&:not(:last-child) {
			padding-bottom: 13px;
		}
	}
`

const Text = styled.div`
	min-width: 0;
	color: #7d7d7d;
	font-size: 16px;
	font-weight: 500;
	line-height: 1.4;
`

const Link = styled(Button).attrs({ variant: "link" })`
	display: inline;
	color: black;
	font-weight: 500;
`

const Strong = styled.strong`
	color: black;
	font-weight: 500;
`

const Date = styled.div`
	flex-shrink: 0;
	color: #666;
	font-size: 13px;
	font-weight: 500;
	line-height: 1.4;
	text-align: right;
	white-space: nowrap;

	@media (max-width: 640px) {
		text-align: left;
	}
`

export default Event
